import { map, type Observable, take } from 'rxjs';
import maxBy from 'lodash/maxBy';
import { HTML_EXPORT_PADDING } from '@ardoq/export';
import type { NonEmptyArray } from '@ardoq/common-helpers';
import { slideCaptureSurface$ } from 'presentation/viewPane/slideCaptureSurface$';
import type { SlideImage } from './slidePlan';
import {
  MAX_SLICES,
  MAX_TALLNESS,
  toCutRow,
  toSliceRanges,
  toSliceImages,
  toStackedImage,
  type SliceRange,
} from './slideImageSlices';

export type TableLayout = {
  height: number;
  headerTop: number;
  headerBottom: number;
  rowBottoms: number[];
};

type Size = { w: number; h: number };

type TableCutsArgs = {
  layout: TableLayout;
  limit: number;
};

type TablePlanArgs = {
  layout: TableLayout;
  image: Size;
  box: Size;
};

type TableSliceArgs = {
  layout: TableLayout;
  box: Size;
};

const SNAP_RADIUS_PX = 4;

const toContentBottom = (container: HTMLElement): number =>
  Math.max(
    0,
    ...Array.from(container.children).map(
      child => child.getBoundingClientRect().bottom
    )
  );

const toBodyRows = (table: HTMLTableElement): Element[] =>
  Array.from(table.querySelectorAll(':scope > tbody > tr'));

export const measureTableLayout = (
  container: HTMLElement
): TableLayout | null => {
  const table = maxBy(
    Array.from(container.querySelectorAll('table')),
    candidate => toBodyRows(candidate).length
  );
  const head = table?.querySelector(':scope > thead');
  const rows = table ? toBodyRows(table) : [];
  if (!head || rows.length === 0) {
    return null;
  }
  const { top } = container.getBoundingClientRect();
  const headRect = head.getBoundingClientRect();
  return {
    height: toContentBottom(container) - top,
    headerTop: headRect.top - top,
    headerBottom: headRect.bottom - top,
    rowBottoms: rows.map(row => row.getBoundingClientRect().bottom - top),
  };
};

export const htmlTableLayout$: Observable<TableLayout | null> =
  slideCaptureSurface$.pipe(
    take(1),
    map(({ html }) =>
      html?.isReport ? measureTableLayout(html.container) : null
    )
  );

export const toImageTableLayout = (
  layout: TableLayout,
  imageHeight: number
): TableLayout => {
  const scale = imageHeight / (layout.height + HTML_EXPORT_PADDING);
  const toImageY = (y: number): number =>
    Math.round((y + HTML_EXPORT_PADDING) * scale);
  return {
    height: imageHeight,
    headerTop: toImageY(layout.headerTop),
    headerBottom: toImageY(layout.headerBottom),
    rowBottoms: layout.rowBottoms.map(toImageY),
  };
};

export const toTableCuts = ({ layout, limit }: TableCutsArgs): number[] => {
  const { height, headerTop, headerBottom, rowBottoms } = layout;
  const headerHeight = headerBottom - headerTop;
  const edges = rowBottoms.slice(0, -1);
  const toCutsFrom = (start: number): number[] => {
    const repeated = start === 0 ? 0 : headerHeight;
    const later = edges.filter(edge => edge > start);
    if (repeated + height - start <= limit || later.length === 0) {
      return [];
    }
    const fitting = later.filter(edge => repeated + edge - start <= limit);
    const cut = fitting.at(-1) ?? later[0];
    return [cut, ...toCutsFrom(cut)];
  };
  return toCutsFrom(0);
};

export const toTablePlan = ({ layout, image, box }: TablePlanArgs): number[] =>
  toTableCuts({ layout, limit: (MAX_TALLNESS * image.w * box.h) / box.w });

export const toPartBands = (
  { headerTop, headerBottom }: TableLayout,
  ranges: NonEmptyArray<SliceRange>
): NonEmptyArray<SliceRange[]> => {
  const header = { top: headerTop, height: headerBottom - headerTop };
  const [first, ...rest] = ranges;
  return [[first], ...rest.map(range => [header, range])];
};

export const toTableSliceImages = (
  context: CanvasRenderingContext2D,
  { layout, box }: TableSliceArgs
): NonEmptyArray<SlideImage> => {
  const { canvas } = context;
  const { width: w, height: h } = canvas;
  const plan = toTablePlan({ layout, image: { w, h }, box });
  if (plan.length >= MAX_SLICES) {
    return toSliceImages(context, box);
  }
  const cuts = plan.map(idealRow =>
    toCutRow({ context, idealRow, radius: SNAP_RADIUS_PX })
  );
  const [first, ...rest] = toPartBands(layout, toSliceRanges(h, cuts));
  return [
    toStackedImage(canvas, first),
    ...rest.map(bands => toStackedImage(canvas, bands)),
  ];
};
